/**
 * <InquiryStatusBadge>
 *
 * Inquiry 任务状态 Badge，还原 Figma 节点 9484-34821 / 2716-71485 设计稿。
 *
 * 三种状态视觉：
 *  - running:   bg=#D1FAE5  icon=Loader(spinning,16px)  text=#065F46
 *  - paused:    bg=#FFEDD5  icon=Dot(6px)               text=#9A3412
 *  - completed: bg=#E5E5E5  icon=Dot(6px,#737373)       text=#262626
 *
 * 供 request-session-card、agent-progress-header 等需要展示 Inquiry 状态的场景复用。
 */

import { FC } from 'react';
import cn from 'classnames';
import { Loader } from 'lucide-react';

export type InquiryStatusBadgeKind = 'running' | 'paused' | 'completed';

export type InquiryStatusBadgeProps = {
  /** 状态 */
  status: InquiryStatusBadgeKind;
  /** 覆盖默认显示文案（Running / Paused / Completed） */
  text?: string;
  className?: string;
};

// ---------------------------------------------------------------------------
// 样式配置（严格对齐 Figma 色值）
// ---------------------------------------------------------------------------

type BadgeStyleConfig = {
  bg: string;
  fg: string;
  /** dot 颜色，与 fg 不同时单独指定 */
  dotColor?: string;
};

const STYLE_MAP: Record<InquiryStatusBadgeKind, BadgeStyleConfig> = {
  running: { bg: '#D1FAE5', fg: '#065F46' },
  paused: { bg: '#FFEDD5', fg: '#9A3412' },
  completed: { bg: '#E5E5E5', fg: '#262626', dotColor: '#737373' },
};

const LABEL_MAP: Record<InquiryStatusBadgeKind, string> = {
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
};

// ---------------------------------------------------------------------------
// 内部 Icon 子组件
// ---------------------------------------------------------------------------

function BadgeIcon({ status, color }: { status: InquiryStatusBadgeKind; color: string }) {
  if (status === 'running') {
    return <Loader size={16} className="animate-spin shrink-0" style={{ color }} />;
  }
  // paused / completed → 6px dot
  return <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export const InquiryStatusBadge: FC<InquiryStatusBadgeProps> = ({ status, text, className }) => {
  const style = STYLE_MAP[status];
  const label = text ?? LABEL_MAP[status];
  const iconColor = style.dotColor ?? style.fg;

  return (
    <span
      className={cn('inline-flex h-6 shrink-0 items-center gap-1 rounded-[8px] px-2', className)}
      style={{ backgroundColor: style.bg }}
    >
      <BadgeIcon status={status} color={iconColor} />
      <span className="text-xs font-medium leading-4" style={{ color: style.fg }}>
        {label}
      </span>
    </span>
  );
};

export default InquiryStatusBadge;
